/**
 * Shared password validation utilities.
 */

/** Checklist labels for default-password forms (registration / user creation). */
export const DEFAULT_PASSWORD_CHECKLIST = [
  { id: 'minLength', label: 'At least 8 characters' },
  { id: 'uppercase', label: 'One uppercase letter' },
  { id: 'lowercase', label: 'One lowercase letter' },
  { id: 'number', label: 'One number (0–9)' },
  { id: 'special', label: 'One special character' },
];

const SPECIAL_CHAR_PATTERN = /[^A-Za-z0-9]/;

/**
 * Evaluates default-password rules: min 8 chars + at least 3 of 4 character categories.
 */
export function evaluateDefaultPassword(password) {
  const value = password || '';
  const checks = {
    minLength: value.length >= 8,
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /\d/.test(value),
    special: SPECIAL_CHAR_PATTERN.test(value),
  };
  const categoryCount = [checks.uppercase, checks.lowercase, checks.number, checks.special].filter(
    Boolean
  ).length;
  const categoriesMet = categoryCount >= 3;
  const isValid = checks.minLength && categoriesMet;

  return { checks, categoryCount, categoriesMet, isValid };
}

export function isDefaultPasswordValid(password) {
  return evaluateDefaultPassword(password).isValid;
}

export function getDefaultPasswordValidationMessage(password) {
  const value = (password || '').trim();
  if (!value) {
    return 'Default password is required';
  }
  if (!isDefaultPasswordValid(value)) {
    return 'Password does not meet the requirements';
  }
  return '';
}

/**
 * Password strength for logged-in change-password (all rules required).
 */
export function getPasswordStrengthErrors(password) {
  const errors = [];
  if (!password || password.length < 8) {
    errors.push('At least 8 characters');
  }
  if (password && !/[A-Z]/.test(password)) {
    errors.push('One uppercase letter');
  }
  if (password && !/[a-z]/.test(password)) {
    errors.push('One lowercase letter');
  }
  if (password && !/\d/.test(password)) {
    errors.push('One number');
  }
  if (password && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('One special character');
  }
  return errors;
}

export function validateLoggedInChangePasswordForm({ currentPassword, newPassword, confirmPassword }) {
  const errors = {};
  const passwordErrors = getPasswordStrengthErrors(newPassword);

  if (!currentPassword) {
    errors.currentPassword = 'Current password is required';
  }
  if (!newPassword) {
    errors.newPassword = 'New password is required';
  } else if (passwordErrors.length > 0) {
    errors.newPassword = `Password must contain: ${passwordErrors.join(', ')}`;
  } else if (currentPassword && newPassword === currentPassword) {
    errors.newPassword = 'New password must be different from current password';
  }
  if (!confirmPassword) {
    errors.confirmPassword = 'Please confirm your new password';
  } else if (newPassword && newPassword !== confirmPassword) {
    errors.confirmPassword = 'New password and confirm password do not match';
  }

  return errors;
}
